/* eslint-disable react/function-component-definition */
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import {
  useGetAllBaseRateFilesQuery,
  useGetBaseRateFileByIdQuery,
  useGetBaseRateFileTermsQuery,
  useUpdateBaseRateFileMutation,
} from "../../../store/queries/baseRateFiles";
import {
  useGetContractFeeSchedulesQuery,
  type IContractFeeSchedule,
  type IContractTermInGroup,
} from "../../../store/queries/contractFeeSchedules";
import { IBaseRateFile } from "../../../types/baseRateFile";

/**
 * Date-only values for PrimeReact Calendar: local noon avoids UTC day-boundary
 * bugs (e.g. May 1 showing as April 30 when the component normalizes to UTC).
 */
function localDateForCalendar(
  y: number,
  monthIndex: number,
  day: number
): Date {
  return new Date(y, monthIndex, day, 12, 0, 0, 0);
}

/** Parse yyyy-mm-dd as a local calendar date for display / Calendar value. */
function parseIsoDateOnlyLocal(raw: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw).trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (!Number.isFinite(y) || mo < 0 || mo > 11 || d < 1 || d > 31) {
    return null;
  }
  const dt = localDateForCalendar(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) {
    return null;
  }
  return dt;
}

function termEffectiveSortKey(t: IBaseRateFile): number {
  const raw = t.effectiveDate;
  if (raw != null && raw !== "") {
    const parsed = parseIsoDateOnlyLocal(String(raw));
    if (parsed) return parsed.getTime();
  }
  if (t.effectiveYear != null) {
    return new Date(t.effectiveYear, 0, 1).getTime();
  }
  return 0;
}

function formatBaseRateTermRowDate(t: IBaseRateFile): string {
  const raw = t.effectiveDate;
  if (raw != null && raw !== "") {
    const local = parseIsoDateOnlyLocal(String(raw));
    if (local) return local.toLocaleDateString();
  }
  if (t.effectiveYear != null) {
    return String(t.effectiveYear);
  }
  return "Date unknown";
}

/** YYYY-MM-DD for an existing term row (duplicate new-term date check vs DB unique key). */
function existingTermEffectiveDateIso(t: IBaseRateFile): string | undefined {
  if (t.effectiveDate != null && String(t.effectiveDate).trim() !== "") {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(t.effectiveDate).trim());
    if (m) return m[1];
  }
  if (t.effectiveYear != null) {
    return `${t.effectiveYear}-01-01`;
  }
  return undefined;
}

function isoDateYmdPrefix(raw: string | null | undefined): string | undefined {
  if (raw == null || String(raw).trim() === "") return undefined;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(raw).trim());
  return m ? m[1] : undefined;
}

/** Trimmed label; uniqueness mirrors API (per account list). */
function baseRateDisplayNameLabel(raw: string): string {
  return raw.trim();
}

function findConflictingBaseRateByDisplayName(
  rows: IBaseRateFile[],
  inputDisplayName: string,
  excludeLogicalFileName: string | undefined,
  isCarveout?: boolean
): IBaseRateFile | undefined {
  const label = baseRateDisplayNameLabel(inputDisplayName);
  if (!label) return undefined;
  const wantCarveout = isCarveout === true;
  return rows.find((f) => {
    if (
      excludeLogicalFileName != null &&
      f.fileName === excludeLogicalFileName
    ) {
      return false;
    }
    if ((f.isCarveout === true) !== wantCarveout) {
      return false;
    }
    return baseRateDisplayNameLabel(f.displayName) === label;
  });
}

function contractReferencesAnyBaseRateTermId(
  contract: IContractFeeSchedule,
  baseTermIds: Set<string>
): boolean {
  return (
    contract.rateCalculations?.some(
      (rc) =>
        rc.baseRateFileId != null &&
        rc.baseRateFileId !== "" &&
        baseTermIds.has(rc.baseRateFileId)
    ) ?? false
  );
}

function contractReferencesCarveoutBaseRateTermId(
  contract: IContractFeeSchedule,
  carveoutTermIds: Set<string>
): boolean {
  const fromDetail = contract.carveOutFiles ?? [];
  const fromList = contract.carveouts ?? [];
  const links = [...fromDetail, ...fromList];
  const seen = new Set<string>();
  return links.some((c) => {
    const bid = c.baseRateFileId;
    if (bid == null || String(bid).trim() === "") return false;
    const key = `${c.id}:${bid}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return carveoutTermIds.has(bid);
  });
}

/** Effective dates (YYYY-MM-DD) already used by this logical file or any contract row referencing its term ids. */
function collectOccupiedNewTermEffectiveDates(
  fileTerms: IBaseRateFile[],
  selected: IBaseRateFile | null,
  contracts: IContractFeeSchedule[],
  baseIds: Set<string>
): Set<string> {
  const s = new Set<string>();
  fileTerms.forEach((t) => {
    const iso = existingTermEffectiveDateIso(t);
    if (iso) s.add(iso);
  });
  if (selected) {
    const iso = existingTermEffectiveDateIso(selected);
    if (iso) s.add(iso);
  }
  contracts.forEach((c) => {
    if (!contractReferencesAnyBaseRateTermId(c, baseIds)) return;
    const iso = isoDateYmdPrefix(c.effectiveDate);
    if (iso) s.add(iso);
  });
  return s;
}

/** Normalize Calendar onChange values to a stable local calendar day (noon). */
function calendarSelectionToLocalDay(
  value: Date | null | undefined
): Date | null {
  if (
    value == null ||
    !(value instanceof Date) ||
    Number.isNaN(value.getTime())
  ) {
    return null;
  }
  return localDateForCalendar(
    value.getFullYear(),
    value.getMonth(),
    value.getDate()
  );
}

function toIsoDateOnly(value: Date | null): string | undefined {
  if (!value || !(value instanceof Date) || Number.isNaN(value.getTime())) {
    return undefined;
  }
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayForCalendar(): Date {
  const n = new Date();
  return localDateForCalendar(n.getFullYear(), n.getMonth(), n.getDate());
}

const VALIDATION_PREFIX = "Base rate file validation failed:";

function parseBaseRateValidationErrors(
  message: string | undefined
): string[] | null {
  if (!message || !message.startsWith(VALIDATION_PREFIX)) return null;
  return message
    .slice(VALIDATION_PREFIX.length)
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

const StyledDialog = styled(Dialog)`
  .selected-files-list {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 0.5rem;
    margin: 1rem 0;
    max-height: 200px;
    overflow-y: auto;

    .selected-file-item {
      display: flex;
      align-items: center;
      padding: 0.5rem;
      border-bottom: 1px solid #f8f9fa;

      &:last-child {
        border-bottom: none;
      }

      .file-name {
        flex: 1;
        margin-right: 1rem;
      }

      .remove-file-btn {
        background: transparent;
        border: none;
        color: #6c757d;
        width: 2rem;
        height: 2rem;
        padding: 0;

        &:hover {
          background: transparent;
          color: #495057;
        }

        &:focus {
          box-shadow: none;
        }
      }
    }
  }
`;

interface IBaseRateFileUpdateDialogProps {
  visible: boolean;
  /** The base rate / carveout file (term row) being updated. Pass this OR fileId. */
  file?: IBaseRateFile | null;
  /** File id to update; the dialog fetches the full file itself (works for any term/version). */
  fileId?: string | null;
  accountId?: string;
  /**
   * When false, the "Upload new term (new effective date)" option is hidden and
   * only a version-update on the existing term is allowed. Used when editing from
   * a previous (non-latest) contract term. Defaults to true.
   */
  allowNewTerm?: boolean;
  onHide: () => void;
  /** Called after a successful update (e.g. to refetch contract terms). */
  onUpdated?: () => void;
}

export const BaseRateFileUpdateDialog: React.FC<
  IBaseRateFileUpdateDialogProps
> = ({
  visible,
  file: fileProp,
  fileId,
  accountId,
  allowNewTerm = true,
  onHide,
  onUpdated,
}) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [updateMode, setUpdateMode] = useState<"same" | "newTerm">("same");
  const [newTermEffectiveDate, setNewTermEffectiveDate] = useState<Date | null>(
    () => todayForCalendar()
  );
  const [updateActivateContracts, setUpdateActivateContracts] = useState(false);
  const [updateRegenerateContracts, setUpdateRegenerateContracts] =
    useState(false);
  /** Base rate row id to PUT when updating the same term (may be an older term than the row opened from). */
  const [updateTargetTermId, setUpdateTargetTermId] = useState<string | null>(
    null
  );

  const [updateFile] = useUpdateBaseRateFileMutation();

  // When only a fileId is provided (e.g. from a contract rate row that may point
  // at a previous term's file), fetch the full file ourselves — findById returns
  // the exact row for any version, so old terms resolve too.
  const { data: fetchedByIdData } = useGetBaseRateFileByIdQuery(fileId ?? "", {
    skip: !visible || !fileId || fileProp != null,
  });
  const file = fileProp ?? fetchedByIdData?.data ?? null;

  // Unfiltered list for uniqueness validation.
  const { data: allBaseRateFilesData } = useGetAllBaseRateFilesQuery({
    accountId,
  });
  const allBaseRateFiles = allBaseRateFilesData?.data || [];

  const { data: fileTerms = [], isFetching: termsLoading } =
    useGetBaseRateFileTermsQuery(
      {
        fileName: file?.fileName ?? "",
        accountId,
      },
      {
        skip: !visible || !file?.fileName,
      }
    );

  const {
    data: contractsForRolloutScan = [],
    isFetching: contractsScanLoading,
  } = useGetContractFeeSchedulesQuery(
    { accountId, groupByContractGroup: false },
    { skip: !visible }
  );

  const isCarveoutUpdateFile = file?.isCarveout === true;

  const baseRateTermIdsForUpdate = useMemo(() => {
    const ids = new Set<string>();
    if (file?.id) ids.add(file.id);
    fileTerms.forEach((t) => ids.add(t.id));
    return ids;
  }, [file?.id, fileTerms]);

  const occupiedNewTermEffectiveDates = useMemo(() => {
    if (!visible || updateMode !== "newTerm") {
      return new Set<string>();
    }
    return collectOccupiedNewTermEffectiveDates(
      fileTerms,
      file,
      contractsForRolloutScan,
      baseRateTermIdsForUpdate
    );
  }, [
    visible,
    updateMode,
    fileTerms,
    file,
    contractsForRolloutScan,
    baseRateTermIdsForUpdate,
  ]);

  const isBaseRateFileUsedInContracts = useMemo(() => {
    if (baseRateTermIdsForUpdate.size === 0) return false;
    return contractsForRolloutScan.some((contract) =>
      contractReferencesAnyBaseRateTermId(contract, baseRateTermIdsForUpdate)
    );
  }, [contractsForRolloutScan, baseRateTermIdsForUpdate]);

  const contractsUsingThisBaseRate = useMemo(() => {
    if (!visible || baseRateTermIdsForUpdate.size === 0) return [];
    const matched = contractsForRolloutScan.filter((c) =>
      contractReferencesAnyBaseRateTermId(c, baseRateTermIdsForUpdate)
    );
    return [...matched].sort((a, b) => {
      const aProv = a.provider?.name?.toString() || "";
      const bProv = b.provider?.name?.toString() || "";
      const provCmp = aProv.localeCompare(bProv, undefined, {
        sensitivity: "base",
      });
      if (provCmp !== 0) return provCmp;
      const aPayer = a.payer?.name?.toString() || "";
      const bPayer = b.payer?.name?.toString() || "";
      return aPayer.localeCompare(bPayer, undefined, { sensitivity: "base" });
    });
  }, [visible, contractsForRolloutScan, baseRateTermIdsForUpdate]);

  const contractsUsingThisCarveout = useMemo(() => {
    if (!visible || baseRateTermIdsForUpdate.size === 0) return [];
    const matched = contractsForRolloutScan.filter((c) =>
      contractReferencesCarveoutBaseRateTermId(c, baseRateTermIdsForUpdate)
    );
    return [...matched].sort((a, b) => {
      const aProv = a.provider?.name?.toString() || "";
      const bProv = b.provider?.name?.toString() || "";
      const provCmp = aProv.localeCompare(bProv, undefined, {
        sensitivity: "base",
      });
      if (provCmp !== 0) return provCmp;
      const aPayer = a.payer?.name?.toString() || "";
      const bPayer = b.payer?.name?.toString() || "";
      return aPayer.localeCompare(bPayer, undefined, { sensitivity: "base" });
    });
  }, [visible, contractsForRolloutScan, baseRateTermIdsForUpdate]);

  const isCarveoutReferencedInContracts = useMemo(() => {
    if (baseRateTermIdsForUpdate.size === 0) return false;
    return contractsForRolloutScan.some((c) =>
      contractReferencesCarveoutBaseRateTermId(c, baseRateTermIdsForUpdate)
    );
  }, [contractsForRolloutScan, baseRateTermIdsForUpdate]);

  const contractDetailHref = (contractId: string) =>
    accountId
      ? `/hospitals/view/${accountId}/contracts/${contractId}`
      : `/payer-rules/contracts/${contractId}`;

  const updateRolloutScanReady =
    visible && !termsLoading && !contractsScanLoading;

  const showRolloutDraftOptions =
    updateMode === "newTerm" &&
    updateRolloutScanReady &&
    isBaseRateFileUsedInContracts &&
    !isCarveoutUpdateFile;

  const fileTermsSortedForPicker = useMemo(
    () =>
      [...fileTerms].sort(
        (a, b) => termEffectiveSortKey(b) - termEffectiveSortKey(a)
      ),
    [fileTerms]
  );

  const updateContextRow = useMemo(() => {
    if (!file) return null;
    if (updateMode !== "same" || !updateTargetTermId) {
      return file;
    }
    return fileTerms.find((t) => t.id === updateTargetTermId) ?? file;
  }, [file, updateMode, updateTargetTermId, fileTerms]);

  const newTermEffectiveDateClash = useMemo(() => {
    if (updateMode !== "newTerm" || !newTermEffectiveDate) {
      return false;
    }
    const iso = toIsoDateOnly(newTermEffectiveDate);
    if (!iso) return false;
    return occupiedNewTermEffectiveDates.has(iso);
  }, [updateMode, newTermEffectiveDate, occupiedNewTermEffectiveDates]);

  const updateDisplayNameConflict = useMemo(() => {
    if (!visible) return undefined;
    return findConflictingBaseRateByDisplayName(
      allBaseRateFiles,
      displayName,
      file?.fileName,
      isCarveoutUpdateFile
    );
  }, [
    visible,
    allBaseRateFiles,
    displayName,
    file?.fileName,
    isCarveoutUpdateFile,
  ]);

  // Seed form state each time the dialog opens for a file.
  useEffect(() => {
    if (visible && file) {
      setDisplayName(file.displayName);
      setUpdateTargetTermId(file.id);
      setUpdateMode("same");
      setNewTermEffectiveDate(todayForCalendar());
      setUpdateActivateContracts(false);
      setUpdateRegenerateContracts(false);
      setSelectedFile(null);
      setValidationErrors([]);
    }
  }, [visible, file]);

  useEffect(() => {
    if (
      visible &&
      (isCarveoutUpdateFile || !allowNewTerm) &&
      updateMode === "newTerm"
    ) {
      setUpdateMode("same");
    }
  }, [visible, isCarveoutUpdateFile, allowNewTerm, updateMode]);

  useEffect(() => {
    if (!showRolloutDraftOptions && updateActivateContracts) {
      setUpdateActivateContracts(false);
    }
  }, [showRolloutDraftOptions, updateActivateContracts]);

  useEffect(() => {
    if (!visible || updateMode !== "same") return;
    if (fileTerms.length === 0) return;
    setUpdateTargetTermId((prev) => {
      if (prev && fileTerms.some((t) => t.id === prev)) return prev;
      const fromOpen = file?.id;
      if (fromOpen && fileTerms.some((t) => t.id === fromOpen)) {
        return fromOpen;
      }
      // The opened file may be an older version of its term anchor (fileTerms
      // returns the latest version per anchor). Match by effective date so a
      // previous term still preselects its own term, not the latest.
      const openIso = isoDateYmdPrefix(file?.effectiveDate);
      if (openIso) {
        const byDate = fileTerms.find(
          (t) => isoDateYmdPrefix(t.effectiveDate) === openIso
        );
        if (byDate) return byDate.id;
      }
      const sorted = [...fileTerms].sort(
        (a, b) => termEffectiveSortKey(b) - termEffectiveSortKey(a)
      );
      return sorted[0]?.id ?? prev;
    });
  }, [visible, updateMode, fileTerms, file?.id, file?.effectiveDate]);

  const resetForm = () => {
    setDisplayName("");
    setSelectedFile(null);
    setUpdateTargetTermId(null);
    setUpdateMode("same");
    setUpdateActivateContracts(false);
    setUpdateRegenerateContracts(false);
    setValidationErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleHide = () => {
    if (isUploading) return;
    resetForm();
    onHide();
  };

  const handleUpdateModeChange = (mode: "same" | "newTerm") => {
    setUpdateMode(mode);
    if (mode === "newTerm") {
      setNewTermEffectiveDate(todayForCalendar());
      setUpdateRegenerateContracts(false);
    }
  };

  const handleUpdate = async () => {
    if (!file || !displayName || !selectedFile) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a display name and select a file",
      });
      return;
    }

    if (updateMode === "newTerm") {
      if (isCarveoutUpdateFile) {
        toast.current?.show({
          severity: "error",
          summary: "Not supported",
          detail:
            "Carveout files cannot create a new term. Use version update on the existing term only.",
        });
        return;
      }
      if (!newTermEffectiveDate) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Please select an effective date for the new base rate term.",
        });
        return;
      }
    }

    const applyRollout = showRolloutDraftOptions && updateActivateContracts;
    const createDraftContracts =
      showRolloutDraftOptions && !updateActivateContracts;

    const newTermIso = toIsoDateOnly(newTermEffectiveDate);

    if (
      updateMode === "newTerm" &&
      newTermIso &&
      occupiedNewTermEffectiveDates.has(newTermIso)
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Duplicate effective date",
        detail:
          "This effective date is already used by a base rate term for this file or by a contract that references this file. Choose a different day.",
      });
      return;
    }

    const dupUpdateLabel = findConflictingBaseRateByDisplayName(
      allBaseRateFiles,
      displayName,
      file.fileName,
      isCarveoutUpdateFile
    );
    if (dupUpdateLabel) {
      toast.current?.show({
        severity: "error",
        summary: "Display name already in use",
        detail: `Another base rate file in this account already uses this display name (file: ${dupUpdateLabel.fileName}). Choose a distinct label.`,
      });
      return;
    }

    const contractEffectiveIso = showRolloutDraftOptions
      ? newTermIso
      : undefined;

    if (showRolloutDraftOptions && !contractEffectiveIso) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "The new term date is invalid. Pick a valid effective date.",
      });
      return;
    }

    const updateRowId =
      updateMode === "same" ? updateTargetTermId ?? file.id : file.id;

    const sendRegenerate = updateMode === "same" && updateRegenerateContracts;

    setIsUploading(true);

    try {
      await updateFile({
        id: updateRowId,
        file: selectedFile,
        displayName,
        accountId,
        ...(updateMode === "newTerm" && newTermEffectiveDate
          ? {
              isNewTerm: true,
              effectiveDate: newTermIso,
            }
          : {}),
        activateContracts: applyRollout,
        contractEffectiveDate: contractEffectiveIso,
        ...(createDraftContracts ? { createDraftContracts: true } : {}),
        ...(sendRegenerate ? { regenerateActiveContracts: true } : {}),
      }).unwrap();

      let updateSuccessDetail: string;
      if (updateMode === "newTerm") {
        if (applyRollout) {
          updateSuccessDetail =
            "New term created. Matching active contracts were rolled forward where applicable.";
        } else if (createDraftContracts) {
          updateSuccessDetail =
            "New term created. Draft contract term(s) were created for affected deals—activate when you are ready to generate procedures.";
        } else {
          updateSuccessDetail =
            "New term created. No contracts reference this logical file yet.";
        }
      } else if (isCarveoutUpdateFile) {
        if (!isCarveoutReferencedInContracts) {
          updateSuccessDetail =
            "Carveout file updated. No contracts attach this file yet.";
        } else if (sendRegenerate) {
          updateSuccessDetail =
            "Carveout file updated. Attached contracts are regenerating in the background—watch the progress badge on this file's Actions row.";
        } else {
          updateSuccessDetail =
            "Carveout file updated. Contracts using this file may show as outdated until you refresh procedures.";
        }
      } else if (!isBaseRateFileUsedInContracts) {
        updateSuccessDetail =
          "Base rate file updated. No contracts reference this file yet.";
      } else if (sendRegenerate) {
        updateSuccessDetail =
          "Base rate file updated. Attached contracts are regenerating in the background—watch the progress badge on this file's Actions row.";
      } else {
        updateSuccessDetail =
          "Base rate file updated. Contracts using this term may show as outdated until you refresh procedures.";
      }

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: updateSuccessDetail,
        life: 6000,
      });

      resetForm();
      onUpdated?.();
      onHide();
    } catch (error: unknown) {
      const d = (error as { data?: Record<string, unknown> })?.data as
        | {
            message?: string;
            messages?: string[];
          }
        | undefined;
      const errText = d?.message ?? d?.messages?.[0];
      const parsed = parseBaseRateValidationErrors(errText);
      if (parsed) {
        setValidationErrors(parsed);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: errText ?? "Failed to update file",
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <StyledDialog
        header={
          isCarveoutUpdateFile
            ? "Update Carveout File"
            : "Update Base Rate File"
        }
        visible={visible}
        onHide={handleHide}
        style={{ width: "650px" }}
        closable={!isUploading}
        closeOnEscape={!isUploading}
      >
        <div className="p-fluid">
          {/* File Information Header */}
          <div className="mb-4 p-3 surface-100 border-round">
            <h6 className="mt-0 mb-2 text-900">{file?.displayName}</h6>
            <div className="flex align-items-center gap-3 text-600">
              <span>
                <i className="pi pi-file mr-1" />
                {file?.fileName}
              </span>
              <span>
                <i className="pi pi-tag mr-1" />
                Version {updateContextRow?.version}
              </span>
            </div>
            {file?.id && (
              <div className="text-xs text-500 font-mono mt-2">
                Row id (ops): {file.id}
              </div>
            )}
          </div>

          <div className="field">
            <label className="font-medium">Contracts using this file</label>
            <div className="p-3 surface-50 border-1 border-200 border-round text-sm max-h-12rem overflow-y-auto">
              {contractsScanLoading || termsLoading ? (
                <span className="text-600">Loading contracts…</span>
              ) : null}
              {!contractsScanLoading &&
              !termsLoading &&
              (isCarveoutUpdateFile
                ? contractsUsingThisCarveout
                : contractsUsingThisBaseRate
              ).length === 0 ? (
                <span className="text-600">
                  {isCarveoutUpdateFile
                    ? "No contracts attach this carveout file (any term)."
                    : "No contracts reference this base rate file (any term)."}
                </span>
              ) : null}
              {!contractsScanLoading &&
              !termsLoading &&
              (isCarveoutUpdateFile
                ? contractsUsingThisCarveout
                : contractsUsingThisBaseRate
              ).length > 0 ? (
                <ul className="list-none m-0 p-0">
                  {(isCarveoutUpdateFile
                    ? contractsUsingThisCarveout
                    : contractsUsingThisBaseRate
                  ).map((c) => {
                    // eslint-disable-next-line no-underscore-dangle
                    const termsInGroupNav = c._termsInGroup;
                    return (
                      <li key={c.id} className="mb-2">
                        <Link
                          to={contractDetailHref(c.id)}
                          state={{
                            termsInGroup: termsInGroupNav as
                              | IContractTermInGroup[]
                              | undefined,
                            contractGroupId: c.contractGroupId,
                          }}
                          className="no-underline"
                        >
                          <div className="font-medium text-primary">
                            {c.provider?.name ?? "—"} → {c.payer?.name ?? "—"}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="field">
            <span className="font-medium block mb-2">Upload type</span>
            <div className="flex flex-column gap-2">
              <div className="flex align-items-center gap-2">
                <RadioButton
                  inputId="modeSame"
                  name="updateMode"
                  value="same"
                  onChange={() => handleUpdateModeChange("same")}
                  checked={updateMode === "same"}
                />
                <label htmlFor="modeSame" className="m-0 cursor-pointer">
                  Update existing term (new version, same effective date)
                </label>
              </div>
              {allowNewTerm && !isCarveoutUpdateFile && (
                <div className="flex align-items-center gap-2">
                  <RadioButton
                    inputId="modeNewTerm"
                    name="updateMode"
                    value="newTerm"
                    onChange={() => handleUpdateModeChange("newTerm")}
                    checked={updateMode === "newTerm"}
                  />
                  <label htmlFor="modeNewTerm" className="m-0 cursor-pointer">
                    Upload new term (new effective date)
                  </label>
                </div>
              )}
              {isCarveoutUpdateFile && (
                <p className="text-600 text-sm m-0">
                  Carveout files only support a version update on an existing
                  term, they do not create new contract terms.
                </p>
              )}
              {!allowNewTerm && !isCarveoutUpdateFile && (
                <p className="text-600 text-sm m-0">
                  This is a previous term — you can update its existing version
                  only. Create new terms from the latest term.
                </p>
              )}
            </div>
          </div>

          <div className="field">
            <label className="font-medium">Terms for this file</label>
            <div className="p-3 surface-50 border-1 border-200 border-round text-sm max-h-10rem overflow-y-auto">
              {termsLoading && <span className="text-600">Loading terms…</span>}
              {!termsLoading && fileTerms.length === 0 && (
                <span className="text-600">No term history loaded.</span>
              )}
              {!termsLoading && fileTerms.length > 0 && (
                <ul className="list-none m-0 p-0">
                  {fileTermsSortedForPicker.map((t) => (
                    <li key={t.id} className="mb-2">
                      <span className="font-medium">
                        {formatBaseRateTermRowDate(t)}
                      </span>
                      {` — v${t.version} — ${t.displayName}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {updateMode === "same" && !termsLoading && fileTerms.length > 0 && (
            <div className="field">
              <label htmlFor="updateTargetTerm" className="font-medium">
                Term to update *
              </label>
              <Dropdown
                inputId="updateTargetTerm"
                value={updateTargetTermId}
                options={fileTermsSortedForPicker.map((t) => ({
                  label: `${formatBaseRateTermRowDate(t)} — v${t.version} — ${
                    t.displayName
                  }`,
                  value: t.id,
                }))}
                onChange={(e) => setUpdateTargetTermId(e.value as string)}
                className="w-full"
                placeholder="Select term"
              />
              <small className="text-600">
                Choose which effective-dated term this CSV replaces; the list
                above is every term for this file name.
              </small>
            </div>
          )}

          {updateMode === "newTerm" && (
            <div className="field">
              <label htmlFor="newTermEffectiveDate" className="font-medium">
                New term effective date *
              </label>
              <Calendar
                id="newTermEffectiveDate"
                value={newTermEffectiveDate}
                onChange={(e) =>
                  setNewTermEffectiveDate(
                    calendarSelectionToLocalDay(e.value as Date | null)
                  )
                }
                showIcon
                className="w-full"
                dateFormat="mm-dd-yy"
              />
              <small className="text-600">
                This same date is used as the contract term effective date when
                you roll forward or create draft terms.
              </small>
              {newTermEffectiveDateClash && (
                <Message
                  severity="error"
                  className="w-full mt-2"
                  text="This effective date is already used by a base rate term for this file or by a contract that references this file. Choose a different day."
                />
              )}
            </div>
          )}

          {/* Display Name Field */}
          <div className="field">
            <label htmlFor="updateDisplayName" className="font-medium">
              Display Name *
            </label>
            <InputText
              id="updateDisplayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter a display name for this base rate file"
              className="w-full"
            />
            {updateDisplayNameConflict && (
              <Message
                severity="error"
                className="w-full mt-2"
                text={`Display name must be unique per account among logical files. Already used by “${updateDisplayNameConflict.displayName}” (${updateDisplayNameConflict.fileName}).`}
              />
            )}
          </div>

          {/* Version Information — same term only */}
          {updateMode === "same" && (
            <div className="field">
              <div className="p-3 surface-50 border-1 border-200 border-round">
                <div className="flex align-items-center justify-content-between mb-2">
                  <span className="font-medium text-900">Version Update</span>
                  <span className="text-600">
                    {updateContextRow?.version} →{" "}
                    {(updateContextRow?.version || 0) + 1}
                  </span>
                </div>
                <p className="text-sm text-600 m-0">
                  Replaces the selected term’s file with a new version (same
                  effective date). Contracts that reference this term may be
                  marked outdated; no draft contract terms are created from this
                  action.
                </p>
              </div>
            </div>
          )}

          {updateMode === "same" && (
            <>
              <div className="field flex align-items-center gap-2">
                <Checkbox
                  inputId="updateRegenerateContracts"
                  checked={updateRegenerateContracts}
                  onChange={(e) => setUpdateRegenerateContracts(!!e.checked)}
                />
                <label
                  htmlFor="updateRegenerateContracts"
                  className="m-0 cursor-pointer"
                >
                  Auto-regenerate attached contracts after upload
                </label>
              </div>
              <p className="text-600 text-sm line-height-3 m-0 mb-3">
                Checked: after upload, each attached active contract is
                regenerated automatically one at a time in the background. The
                base rate file’s Actions row shows live progress (e.g. 3/10) and
                the Outdated label on each contract clears as it finishes.
              </p>
            </>
          )}

          {!updateRolloutScanReady && (
            <p className="text-600 text-sm">
              Checking contract usage for this file…
            </p>
          )}
          {updateMode === "newTerm" &&
            updateRolloutScanReady &&
            !isBaseRateFileUsedInContracts && (
              <p className="text-600 text-sm line-height-3 p-3 surface-50 border-round border-1 border-200">
                No contracts currently reference this base rate file (any term).
                Roll forward and draft options below appear after at least one
                contract rate line uses it.
              </p>
            )}
          {showRolloutDraftOptions && (
            <>
              <div className="field flex align-items-center gap-2">
                <Checkbox
                  inputId="updateActivateContracts"
                  checked={updateActivateContracts}
                  onChange={(e) => setUpdateActivateContracts(!!e.checked)}
                />
                <label
                  htmlFor="updateActivateContracts"
                  className="m-0 cursor-pointer"
                >
                  Roll forward active contracts that use older terms of this
                  logical file
                </label>
              </div>
              <p className="text-600 text-sm line-height-3 m-0 mb-2">
                Unchecked: the server should create <strong>Draft</strong>{" "}
                contract term(s) for affected provider/payer deals using this
                logical file—no provider procedures until you activate those
                drafts. Checked: roll active terms forward (per server rules).
                The new term date above is used as the contract effective date.
              </p>
            </>
          )}

          {/* File Upload Section */}
          <div className="field">
            <label className="font-medium">New Base Rate File (CSV) *</label>

            {!selectedFile ? (
              <div
                className="p-4 border-2 border-dashed border-300 border-round text-center cursor-pointer hover:border-400 transition-colors"
                onClick={() =>
                  document.getElementById("file-update-input")?.click()
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    document.getElementById("file-update-input")?.click();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Click to select a new CSV file"
              >
                <i className="pi pi-cloud-upload text-4xl text-400 mb-3" />
                <p className="text-600 mb-2">Click to select a new CSV file</p>
                <p className="text-sm text-500">or drag and drop here</p>
              </div>
            ) : (
              <div className="selected-files-list">
                <div className="selected-file-item bg-primary-50 border-primary-200">
                  <i className="pi pi-file text-primary mr-2" />
                  <span className="file-name text-primary font-medium flex-1">
                    {selectedFile.name}
                  </span>
                  <span className="text-sm text-600 mr-2">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-plain remove-file-btn"
                    onClick={() => setSelectedFile(null)}
                  />
                </div>
              </div>
            )}

            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setSelectedFile(e.target.files[0]);
                  setValidationErrors([]);
                  e.target.value = "";
                }
              }}
              style={{ display: "none" }}
              id="file-update-input"
            />
          </div>

          {/* Warning Message */}
          <div className="p-3 border-left-3 border-orange-500 surface-ground">
            <div className="flex align-items-start">
              <i className="pi pi-exclamation-triangle text-orange-500 mr-2 mt-1" />
              <div>
                <h6 className="mt-0 mb-2 text-900">Important</h6>
                <p className="text-sm text-700 m-0 line-height-3">
                  {updateMode === "same" &&
                    "Replacing this term’s file does not create draft contract terms. Active contracts that use these rates may show as outdated until you refresh procedures."}
                  {updateMode === "newTerm" &&
                    !showRolloutDraftOptions &&
                    "This file is not referenced by any contract rate lines yet, so no contract rollout or draft creation runs from this new-term update."}
                  {showRolloutDraftOptions &&
                    "With roll forward off, the API should create Draft contract term(s). Contract effective date matches the new base rate term date. Provider procedures are not generated for Draft until you activate."}
                </p>
              </div>
            </div>
          </div>

          {validationErrors.length > 0 && (
            <div className="mt-3 p-3 border-round surface-100 border-1 border-red-300">
              <div className="flex align-items-center gap-2 mb-2 text-red-700 font-semibold">
                <i className="pi pi-exclamation-triangle" />
                File validation failed
              </div>
              <ul className="m-0 pl-4 text-sm line-height-3">
                {validationErrors.map((err, i) => (
                  <li key={i} className="mb-1 text-red-700">
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={handleHide}
              disabled={isUploading}
            />
            <Button
              label={isUploading ? "Updating..." : "Update File"}
              icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
              onClick={handleUpdate}
              disabled={
                isUploading ||
                !selectedFile ||
                !displayName.trim() ||
                !!updateDisplayNameConflict ||
                newTermEffectiveDateClash ||
                (updateMode === "same" &&
                  fileTerms.length > 0 &&
                  !updateTargetTermId)
              }
              loading={isUploading}
            />
          </div>
        </div>
      </StyledDialog>
    </>
  );
};

export default BaseRateFileUpdateDialog;
